import { db } from '@/services/db';
import store from '@/store';
import axios from 'axios';
import uuid from '@/plugins/uuid';
import imageFileManager from '@/utils/imageFileManager';

class ImageUploadService {
    constructor() {
        // Make these public so they can be set from outside
        this.apiUrl = null;
        this.bearerToken = null;
    }

    /**
     * Initialize the service with API credentials
     */
    init() {
        this.apiUrl = store.getters.getApiUrl;
        this.bearerToken = store.getters.getBearer;
        
        // If bearer is not available from getBearer, try other getters
        if (!this.bearerToken) {
            // Try different getter names that might be used
            this.bearerToken = store.state.bearer || 
                              store.state.auth?.bearer || 
                              store.state.user?.bearer ||
                              store.getters.getBearerToken ||
                              store.getters.getAuthToken;
        }
        
        console.log('ImageUploadService initialized:', {
            apiUrl: this.apiUrl,
            hasToken: !!this.bearerToken,
            tokenLength: this.bearerToken ? this.bearerToken.length : 0,
            bearer: this.bearerToken || 'NOT FOUND'
        });
    }

    /**
     * Check if we're online (respects force offline mode)
     */
    get isOnline() {
        return store.getters.online;
    }

    /**
     * Convert base64 to blob
     */
    base64ToBlob(base64String, mimeType = 'image/jpeg') {
        // Remove data URL prefix if present
        const base64Data = base64String.replace(/^data:image\/\w+;base64,/, '');
        
        // Decode base64 string
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        
        return new Blob([bytes], { type: mimeType });
    }

    /**
     * Extract MIME type from base64 string
     */
    getMimeType(base64String) {
        const match = base64String.match(/^data:([^;]+);base64,/);
        return match ? match[1] : 'image/jpeg';
    }

    /**
     * Get file extension from MIME type
     */
    getExtension(mimeType) {
        const extensions = {
            'image/jpeg': 'jpg',
            'image/jpg': 'jpg',
            'image/png': 'png',
            'image/gif': 'gif',
            'image/webp': 'webp'
        };
        return extensions[mimeType] || 'jpg';
    }

    /**
     * Request presigned URL from backend
     */
    async requestPresignedUrl(entityType, entityUuid, fileName, fileSize, fileType, imageUuid = null, existingImageUuid = null) {
        // Check if we're online first (respects force offline mode)
        if (!this.isOnline) {
            throw new Error('Cannot request presigned URL while offline or in force offline mode');
        }

        // Try to init if credentials are missing
        if (!this.apiUrl || !this.bearerToken) {
            this.init();
        }
        
        // Final check - if still no bearer token, throw error
        if (!this.bearerToken) {
            throw new Error('No authentication token available. Please ensure you are logged in.');
        }

        console.log('Requesting presigned URL:', {
            apiUrl: this.apiUrl,
            entityType,
            entityUuid,
            fileName,
            fileSize,
            fileType,
            imageUuid,
            existingImageUuid
        });

        try {
            // Remove trailing slash if present and check if apiUrl already ends with /api
            const baseUrl = this.apiUrl.replace(/\/$/, '');
            const url = baseUrl.endsWith('/api') 
                ? `${baseUrl}/images/upload/prepare`
                : `${baseUrl}/api/images/upload/prepare`;
            
            const requestBody = {
                entity_type: entityType,
                entity_uuid: entityUuid,
                file_name: fileName,
                file_size: fileSize,
                file_type: fileType
            };
            
            // Include image_uuid if provided
            if (imageUuid) {
                requestBody.image_uuid = imageUuid;
            }
            
            // Include existing_image_uuid for update operations
            if (existingImageUuid) {
                requestBody.existing_image_uuid = existingImageUuid;
            }
            
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.bearerToken}`,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Presigned URL request failed:', {
                    status: response.status,
                    statusText: response.statusText,
                    body: errorText,
                    url: url,
                    bearer: this.bearerToken ? 'Bearer token present' : 'No bearer token'
                });
                
                // Check for specific error codes
                if (response.status === 401) {
                    console.error('Authentication failed. Token may be invalid or expired.');
                    throw new Error('Authentication failed (401) - Please check login status');
                }
                
                if (response.status === 404) {
                    console.warn('Presigned URL endpoint not found. The backend may not be ready yet.');
                    throw new Error('Presigned URL endpoint not available (404)');
                }
                
                throw new Error(`Failed to get presigned URL: ${response.status} - ${errorText}`);
            }

            const data = await response.json();
            console.log('Received presigned URL response:', data);
            
            // Ensure we have the expected structure
            if (!data.upload_url && !data.url) {
                throw new Error('No upload URL in response');
            }
            
            // Normalize the response structure to match what we get from backend
            return {
                upload_url: data.upload_url || data.url,
                upload_fields: data.fields || data.upload_fields || {},
                session_uuid: data.session_uuid,  // This is what backend actually returns
                upload_id: data.upload_id || data.session_uuid,  // Use session_uuid as upload_id
                image_uuid: data.image_uuid,  // May not be present
                s3_key: data.s3_key || data.key || (data.fields && data.fields.key),
                expires_at: data.expires_at
            };
        } catch (error) {
            console.error('Error requesting presigned URL:', error);
            throw error;
        }
    }

    /**
     * Upload image to S3 using presigned URL
     */
    async uploadToS3(uploadUrl, uploadFields, imageBlob, onProgress = null) {
        try {
            console.log('Uploading to S3:', {
                uploadUrl,
                fields: uploadFields,
                blobSize: imageBlob.size,
                blobType: imageBlob.type
            });

            const formData = new FormData();

            // Get the actual content type from the blob
            const contentType = imageBlob.type || 'image/jpeg';

            // Check if backend provided acl and Content-Type in fields
            const hasAcl = uploadFields && 'acl' in uploadFields;
            const hasContentType = uploadFields && 'Content-Type' in uploadFields;

            // Add acl if not provided by backend (required by S3 policy)
            if (!hasAcl) {
                formData.append('acl', 'private');
            }

            // Add Content-Type if not provided by backend (required by S3 policy)
            if (!hasContentType) {
                formData.append('Content-Type', contentType);
            }

            // IMPORTANT: Add fields in the exact order they came from the backend
            // The order matters for S3 presigned POST
            if (uploadFields) {
                // Add fields in the order they appear
                Object.entries(uploadFields).forEach(([key, value]) => {
                    if (value !== null && value !== undefined) {
                        formData.append(key, value);
                    }
                });
            }

            // Add the file as the last field (MUST be last for S3)
            formData.append('file', imageBlob, 'image.jpg');

            // Use fetch instead of axios to avoid CORS issues
            // S3 presigned POST doesn't work well with axios's default headers
            const response = await fetch(uploadUrl, {
                method: 'POST',
                body: formData,
                // Don't set Content-Type header - let browser set it with boundary
            });

            console.log('S3 upload response:', {
                status: response.status,
                statusText: response.statusText,
                ok: response.ok
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('S3 upload failed:', errorText);
                
                // Check for expired presigned URL
                if (response.status === 403 && 
                    (errorText.includes('Request has expired') || 
                     errorText.includes('expired') ||
                     errorText.includes('Token expired'))) {
                    const expiredError = new Error('EXPIRED_URL: Presigned URL has expired');
                    expiredError.isExpiredUrl = true;
                    throw expiredError;
                }
                
                // Check for specific S3 policy errors that shouldn't be retried
                if (response.status === 403 || errorText.includes('AccessDenied') || 
                    errorText.includes('Policy Condition failed') || 
                    errorText.includes('Invalid according to Policy')) {
                    const policyError = new Error(`S3 Policy Error: ${errorText.substring(0, 200)}`);
                    policyError.isPolicyError = true;
                    policyError.permanent = true;
                    throw policyError;
                }
                
                throw new Error(`S3 upload failed: ${response.status} - ${errorText}`);
            }

            // Report progress as complete
            if (onProgress) {
                onProgress(100);
            }

            return true;
        } catch (error) {
            // Check if it's a CORS error
            if (error.message && error.message.includes('CORS')) {
                console.error('CORS error detected. S3 bucket needs CORS configuration:', error);
                // Throw a specific CORS error
                const corsError = new Error('CORS_ERROR: S3 bucket is not configured for cross-origin requests');
                corsError.isCorsError = true;
                throw corsError;
            }
            
            // Check if it's a network error that might be CORS-related
            if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
                console.error('Failed to fetch - likely CORS error:', error);
                const corsError = new Error('CORS_ERROR: Unable to upload to S3 - CORS policy blocking request');
                corsError.isCorsError = true;
                throw corsError;
            }
            
            console.error('Error uploading to S3:', error);
            throw error;
        }
    }

    /**
     * Confirm upload with backend
     */
    async confirmUpload(sessionUuid, imageUuid, entityType, entityUuid, s3Key, title = null, orientation = 0, existingImageUuid = null) {
        // Check if we're online first (respects force offline mode)
        if (!this.isOnline) {
            throw new Error('Cannot confirm upload while offline or in force offline mode');
        }

        if (!this.apiUrl || !this.bearerToken) {
            this.init();
        }

        try {
            // Remove trailing slash if present and check if apiUrl already ends with /api
            const baseUrl = this.apiUrl.replace(/\/$/, '');
            const url = baseUrl.endsWith('/api')
                ? `${baseUrl}/images/upload/confirm`
                : `${baseUrl}/api/images/upload/confirm`;
            
            const requestBody = {
                session_uuid: sessionUuid,  // Backend expects session_uuid, not upload_id
                entity_type: entityType,
                entity_uuid: entityUuid,
                image_uuid: imageUuid,
                s3_key: s3Key,
                title: title,
                orientation: orientation
            };
            
            // Include existing_image_uuid for update operations
            if (existingImageUuid) {
                requestBody.existing_image_uuid = existingImageUuid;
            }
            
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.bearerToken}`,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorText = await response.text();
                
                // Check if it's a duplicate UUID error (can be 500 or 409)
                if ((response.status === 500 || response.status === 409 || response.status === 422) && 
                    (errorText.includes('Duplicate entry') && errorText.includes('images_uuid_unique') ||
                     errorText.includes('Duplicate entry') && errorText.includes('abe70f25-1033-4d04-9acb-bfc00836b930'))) {
                    console.warn(`Image ${imageUuid} already confirmed on backend with different session, treating as success`);
                    // Throw a specific error that we can catch
                    const duplicateError = new Error('Image already exists');
                    duplicateError.isDuplicate = true;
                    throw duplicateError;
                }
                
                throw new Error(`Failed to confirm upload: ${response.status} - ${errorText.substring(0, 200)}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error confirming upload:', error);
            throw error;
        }
    }

    /**
     * Main upload flow for a single image with retry logic
     */
    async uploadImage(image, entityType, entityUuid, onProgress = null, retryCount = 0) {
        console.log('Starting uploadImage:', {
            imageUuid: image.uuid,
            entityType,
            entityUuid,
            hasBase64: !!image.base64,
            retryCount,
            isOnline: this.isOnline,
            forceOffline: store.getters.getForceOffline,
            currentStatus: image.upload_status
        });

        // Check if already confirmed - no need to re-upload
        if (image.upload_status === 'confirmed') {
            console.log(`Image ${image.uuid} already confirmed, skipping upload`);
            return true;
        }

        // Check if we're online (respects force offline mode)
        if (!this.isOnline) {
            console.log('Not online or force offline is enabled, queueing image for later upload');
            await this.queueForLaterUpload(image, entityType, entityUuid);
            return false;
        }

        const MAX_RETRIES = 3;
        const RETRY_DELAY_BASE = 1000; // Start with 1 second

        try {
            // Check if image has base64 data, try to recover from local file if missing
            if (!image.base64) {
                console.log(`Image ${image.uuid} missing base64, attempting to recover from local file`);
                
                // Try to read from local file system (mobile only)
                const localBase64 = await imageFileManager.readImageFromFile(image.uuid);
                
                if (localBase64) {
                    console.log(`Successfully recovered image ${image.uuid} from local file`);
                    image.base64 = localBase64;
                    
                    // Update the database with recovered base64
                    await db.images.update(image.uuid, { base64: localBase64 });
                } else {
                    throw new Error('Image missing base64 data and not found in local storage');
                }
            }

            // Update status to uploading
            await db.images.update(image.uuid, { upload_status: 'uploading' });

            // Get MIME type and create blob
            const mimeType = this.getMimeType(image.base64);
            const imageBlob = this.base64ToBlob(image.base64, mimeType);
            const extension = this.getExtension(mimeType);
            // Use the image's UUID in the filename - this should match what backend expects
            const fileName = `image-${image.uuid}.${extension}`;

            console.log('Prepared image data:', {
                imageUuid: image.uuid,
                mimeType,
                blobSize: imageBlob.size,
                extension,
                fileName
            });

            // Request presigned URL
            const presignedResponse = await this.requestPresignedUrl(
                entityType,
                entityUuid,
                fileName,
                imageBlob.size,
                mimeType,
                image.uuid  // Pass the image UUID to the request
            );

            // Try to upload to S3
            try {
                const uploadSuccess = await this.uploadToS3(
                    presignedResponse.upload_url,
                    presignedResponse.upload_fields,
                    imageBlob,
                    onProgress
                );

                if (!uploadSuccess) {
                    throw new Error('S3 upload failed');
                }
            } catch (uploadError) {
                // Check if it's an expired URL error
                if (uploadError.isExpiredUrl) {
                    console.log('Presigned URL expired, requesting new URL and retrying...');
                    
                    // Request a new presigned URL
                    const newPresignedResponse = await this.requestPresignedUrl(
                        entityType,
                        entityUuid,
                        fileName,
                        imageBlob.size,
                        mimeType,
                        image.uuid
                    );
                    
                    // Try upload with new URL
                    const uploadSuccess = await this.uploadToS3(
                        newPresignedResponse.upload_url,
                        newPresignedResponse.upload_fields,
                        imageBlob,
                        onProgress
                    );
                    
                    if (!uploadSuccess) {
                        throw new Error('S3 upload failed with new presigned URL');
                    }
                    
                    // Update presignedResponse for confirmation
                    Object.assign(presignedResponse, newPresignedResponse);
                } else {
                    // Re-throw other errors
                    throw uploadError;
                }
            }

            // Update local database with S3 key
            await db.images.update(image.uuid, { 
                s3_key: presignedResponse.s3_key,
                upload_status: 'uploaded'
            });

            // Confirm with backend using session_uuid
            try {
                await this.confirmUpload(
                    presignedResponse.session_uuid,  // Use session_uuid from prepare response
                    image.uuid,
                    entityType,
                    entityUuid,
                    presignedResponse.s3_key,
                    image.title,
                    image.orientation
                );

                // Update final status but keep base64 until URL is available (for UI display)
                await db.images.update(image.uuid, {
                    upload_status: 'confirmed',
                    sync_status: 'synced'
                    // Keep base64 so the image can still be displayed until next sync provides URL
                });
            } catch (confirmError) {
                // If it's a duplicate UUID error, treat as success since image is already on backend
                if (confirmError.isDuplicate || (confirmError.message && confirmError.message.includes('Image already exists'))) {
                    console.log(`Image ${image.uuid} already exists on backend, marking as confirmed`);
                    await db.images.update(image.uuid, {
                        upload_status: 'confirmed',
                        sync_status: 'synced'
                        // Keep base64 so the image can still be displayed until next sync provides URL
                    });
                    return true;
                }
                // Set status to failed before re-throwing
                await db.images.update(image.uuid, {
                    upload_status: 'failed',
                    upload_error: `Confirm failed: ${confirmError.message}`
                });
                throw confirmError;
            }

            // Clean up local file after successful upload (if local backup is enabled)
            // Only delete if we're sure the upload is complete and confirmed
            if (store.getters.getImageSaveLocalBackup) {
                try {
                    // Optional: Keep the file for a while even after successful upload
                    // For now, we'll keep it until manual cleanup
                    // await imageFileManager.deleteImageFile(image.uuid);
                    console.log(`Image ${image.uuid} successfully uploaded, local file retained for safety`);
                } catch (error) {
                    console.error('Error during local file cleanup:', error);
                }
            }

            return true;
        } catch (error) {
            console.error('Error in uploadImage:', error);
            
            // Check if it's a CORS error
            if (error.isCorsError || error.message.includes('CORS_ERROR')) {
                // Mark as permanently failed - CORS issues won't resolve with retries
                await db.images.update(image.uuid, { 
                    upload_status: 'cors_error',
                    upload_error: 'CORS configuration error - S3 bucket needs to be configured for cross-origin requests'
                });
                
                console.error('CORS error detected - not queuing for retry as this requires backend configuration');
                // Don't queue for retry - this needs backend intervention
                throw error;
            }
            
            // Check if it's an S3 Policy error (like wrong Content-Type)
            if (error.isPolicyError || error.message.includes('Policy Error') || 
                error.message.includes('Policy Condition failed')) {
                // Mark as permanently failed - Policy errors won't resolve with retries
                await db.images.update(image.uuid, { 
                    upload_status: 'policy_error',
                    upload_error: error.message.substring(0, 200)
                });
                
                console.error('S3 Policy error detected - not retrying:', error.message);
                // Don't queue for retry - this needs fixing the image format or backend policy
                throw error;
            }
            
            // Check if we should retry (for transient errors)
            if (retryCount < MAX_RETRIES && !error.permanent) {
                const delay = RETRY_DELAY_BASE * Math.pow(2, retryCount); // Exponential backoff
                console.log(`Retrying upload after ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);
                
                // Update status to show retry
                await db.images.update(image.uuid, { 
                    upload_status: 'retrying',
                    upload_error: `Retry ${retryCount + 1}/${MAX_RETRIES}: ${error.message}`
                });
                
                // Wait before retrying
                await new Promise(resolve => setTimeout(resolve, delay));
                
                // Retry the upload
                return this.uploadImage(image, entityType, entityUuid, onProgress, retryCount + 1);
            }
            
            // Update status to failed
            await db.images.update(image.uuid, { 
                upload_status: 'failed',
                upload_error: error.message
            });

            // If offline, queue for later
            if (!this.isOnline) {
                await this.queueForLaterUpload(image, entityType, entityUuid);
            }

            throw error;
        }
    }

    /**
     * Queue image for later upload when offline
     */
    async queueForLaterUpload(image, entityType, entityUuid) {
        const jobUuid = uuid();
        await db.jobs.put({
            uuid: jobUuid,
            type: 'image-s3-upload',
            module: 'images',
            url: null,
            attempt: 0,  // Initialize attempt counter
            payload: {
                uuid: jobUuid,
                image_uuid: image.uuid,
                imageable_uuid: entityUuid,
                entity_type: entityType
            },
            created_at: new Date().toISOString()
        });
    }

    /**
     * Process all pending image uploads with concurrency limit
     * @param {number} concurrencyLimit - Maximum number of parallel uploads (default: 3)
     */
    async processPendingUploads(concurrencyLimit = 3) {
        if (!this.isOnline) {
            console.log('Cannot process uploads while offline');
            return;
        }

        try {
            // Get all images pending upload
            const pendingImages = await db.images
                .where('upload_status')
                .anyOf(['pending', 'failed'])
                .toArray();

            console.log(`Processing ${pendingImages.length} pending image uploads with concurrency limit of ${concurrencyLimit}`);

            // Process images in batches with concurrency limit
            const results = await this.processBatchWithConcurrency(
                pendingImages,
                concurrencyLimit,
                async (image) => {
                    try {
                        // Find the entity type from the imageable_uuid
                        const entityInfo = await this.findEntityInfo(image.imageable_uuid);
                        if (entityInfo) {
                            await this.uploadImage(
                                image,
                                entityInfo.type,
                                image.imageable_uuid
                            );
                            return { success: true, uuid: image.uuid };
                        }
                        return { success: false, uuid: image.uuid, error: 'Entity not found' };
                    } catch (error) {
                        console.error(`Failed to upload image ${image.uuid}:`, error);
                        return { success: false, uuid: image.uuid, error: error.message };
                    }
                }
            );

            const successful = results.filter(r => r.success).length;
            const failed = results.filter(r => !r.success).length;
            console.log(`Pending uploads processed: ${successful} successful, ${failed} failed`);

            return results;
        } catch (error) {
            console.error('Error processing pending uploads:', error);
            throw error;
        }
    }

    /**
     * Process array items in batches with concurrency limit
     * @param {Array} items - Items to process
     * @param {number} concurrencyLimit - Maximum concurrent operations
     * @param {Function} processFn - Async function to process each item
     */
    async processBatchWithConcurrency(items, concurrencyLimit, processFn) {
        const results = [];
        const executing = [];

        for (const item of items) {
            // Create promise for this item
            const promise = processFn(item).then(result => {
                // Remove from executing array when done
                executing.splice(executing.indexOf(promise), 1);
                return result;
            });

            results.push(promise);
            executing.push(promise);

            // If we've reached concurrency limit, wait for one to finish
            if (executing.length >= concurrencyLimit) {
                await Promise.race(executing);
            }
        }

        // Wait for all remaining promises to complete
        return Promise.all(results);
    }

    /**
     * Find entity type and info from UUID
     */
    async findEntityInfo(uuid) {
        // Check observations
        const observation = await db.observations.where('uuid').equals(uuid).first();
        if (observation) return { type: 'observation', entity: observation };

        // Check measurements
        const measurement = await db.measurements.where('uuid').equals(uuid).first();
        if (measurement) return { type: 'measurement', entity: measurement };

        // Check location_item_components
        const component = await db.location_item_components.where('uuid').equals(uuid).first();
        if (component) return { type: 'location_item_component', entity: component };

        // Check locations
        const location = await db.locations.where('uuid').equals(uuid).first();
        if (location) return { type: 'location', entity: location };

        return null;
    }

    /**
     * Cancel an upload
     */
    async cancelUpload(imageUuid) {
        await db.images.update(imageUuid, { 
            upload_status: 'cancelled'
        });
    }

    /**
     * Retry a failed upload
     */
    async retryUpload(imageUuid) {
        const image = await db.images.get(imageUuid);
        if (!image) {
            throw new Error('Image not found');
        }

        const entityInfo = await this.findEntityInfo(image.imageable_uuid);
        if (!entityInfo) {
            throw new Error('Entity not found for image');
        }

        return this.uploadImage(image, entityInfo.type, image.imageable_uuid);
    }

    /**
     * Update an existing image with new image data
     * This replaces the S3 file while keeping the same image UUID
     * @param {string} existingImageUuid - UUID of the image to replace
     * @param {Object} newImageData - New image data with base64
     * @param {string} entityType - Type of entity (observation, measurement, etc.)
     * @param {string} entityUuid - UUID of the entity
     * @param {Function} onProgress - Optional progress callback
     * @param {number} retryCount - Current retry attempt
     */
    async updateImage(existingImageUuid, newImageData, entityType, entityUuid, onProgress = null, retryCount = 0) {
        console.log('Starting updateImage:', {
            existingImageUuid,
            entityType,
            entityUuid,
            hasBase64: !!newImageData.base64,
            retryCount,
            isOnline: this.isOnline
        });

        // The image must still exist locally to update it. Check on EVERY attempt
        // (it could be deleted between retries) so we never push a ghost update for a
        // record that's already gone.
        const existingRecord = await db.images.get(existingImageUuid);
        if (!existingRecord) {
            console.warn(`updateImage: image ${existingImageUuid} no longer exists locally; skipping update`);
            return false;
        }

        // Status-based guard: an update can only succeed if the image already exists
        // on the server. We don't see the backend's "existing image not found"
        // message in this app, so we decide from our own local state. Only on the
        // first attempt (retryCount === 0), before this method mutates upload_status
        // to 'uploading'/'retrying'.
        if (retryCount === 0) {
            const status = existingRecord.upload_status;
            // 'uploading'/'uploaded' = an upload is actively running; 'retrying' = an
            // upload/create is between retry attempts (set in uploadImage/updateImage
            // around the backoff). In all three an attempt is in flight, so starting a
            // create here would race it and could lose the annotation. Defer instead:
            // throw to keep the job queued. A later sync cycle retries once the image
            // settles to 'confirmed' (-> real update) or 'failed' (-> create fallback).
            if (status === 'uploading' || status === 'uploaded' || status === 'retrying') {
                console.warn(`updateImage: image ${existingImageUuid} upload still in progress (status: ${status}); deferring update`);
                throw new Error(`Image ${existingImageUuid} upload in progress; deferring update`);
            }
            if (status !== 'confirmed') {
                // Not on the backend and no upload in progress -> upload as a new
                // image (create) with the annotated base64, reusing the same UUID.
                console.warn(`updateImage: image ${existingImageUuid} not confirmed on server (status: ${status}); uploading as a new image instead of updating`);
                // Persist the edited fields so they survive an offline re-queue: if
                // uploadImage goes offline it queues a create job that later re-reads
                // the image from the DB, so title/orientation must be in the DB too -
                // not just on the in-memory object we pass below.
                const dbUpdate = { base64: newImageData.base64 };
                if (newImageData.title != null) dbUpdate.title = newImageData.title;
                if (newImageData.orientation != null) dbUpdate.orientation = newImageData.orientation;
                await db.images.update(existingImageUuid, dbUpdate);
                const imageForUpload = {
                    ...existingRecord,
                    ...dbUpdate,
                    imageable_uuid: entityUuid,
                };
                // uploadImage handles online/offline (queues a create job if offline).
                return this.uploadImage(imageForUpload, entityType, entityUuid, onProgress);
            }
        }

        // Check if we're online (respects force offline mode)
        if (!this.isOnline) {
            console.log('Not online or force offline is enabled, queueing image update for later');
            await this.queueForLaterUpdate(existingImageUuid, newImageData, entityType, entityUuid);
            return false;
        }

        const MAX_RETRIES = 3;
        const RETRY_DELAY_BASE = 1000;

        try {
            // Check if new image has base64 data
            if (!newImageData.base64) {
                throw new Error('New image missing base64 data');
            }

            // Update status in database
            await db.images.update(existingImageUuid, { 
                upload_status: 'uploading',
                base64: newImageData.base64 // Store new base64
            });

            // Get MIME type and create blob
            const mimeType = this.getMimeType(newImageData.base64);
            const imageBlob = this.base64ToBlob(newImageData.base64, mimeType);
            const extension = this.getExtension(mimeType);
            const fileName = `image-${uuid()}.${extension}`; // Generate new filename for the update

            console.log('Prepared updated image data:', {
                existingImageUuid,
                mimeType,
                blobSize: imageBlob.size,
                extension,
                fileName
            });

            // Request presigned URL with existing_image_uuid for update
            const presignedResponse = await this.requestPresignedUrl(
                entityType,
                entityUuid,
                fileName,
                imageBlob.size,
                mimeType,
                uuid(), // New image UUID for the uploaded file
                existingImageUuid  // Pass the existing image UUID to replace
            );

            // Upload to S3
            try {
                const uploadSuccess = await this.uploadToS3(
                    presignedResponse.upload_url,
                    presignedResponse.upload_fields,
                    imageBlob,
                    onProgress
                );

                if (!uploadSuccess) {
                    throw new Error('S3 upload failed');
                }
            } catch (uploadError) {
                // Handle expired URL
                if (uploadError.isExpiredUrl) {
                    console.log('Presigned URL expired, requesting new URL and retrying...');
                    
                    const newPresignedResponse = await this.requestPresignedUrl(
                        entityType,
                        entityUuid,
                        fileName,
                        imageBlob.size,
                        mimeType,
                        uuid(),
                        existingImageUuid
                    );
                    
                    const uploadSuccess = await this.uploadToS3(
                        newPresignedResponse.upload_url,
                        newPresignedResponse.upload_fields,
                        imageBlob,
                        onProgress
                    );
                    
                    if (!uploadSuccess) {
                        throw new Error('S3 upload failed with new presigned URL');
                    }
                    
                    Object.assign(presignedResponse, newPresignedResponse);
                } else {
                    throw uploadError;
                }
            }

            // Update local database with new S3 key
            await db.images.update(existingImageUuid, { 
                s3_key: presignedResponse.s3_key,
                upload_status: 'uploaded',
                updated_at: new Date().toISOString()
            });

            // Confirm with backend
            try {
                await this.confirmUpload(
                    presignedResponse.session_uuid,
                    presignedResponse.image_uuid,
                    entityType,
                    entityUuid,
                    presignedResponse.s3_key,
                    newImageData.title,
                    newImageData.orientation || 0,
                    existingImageUuid  // Pass existing image UUID for update
                );

                // Update final status but keep base64 until URL is available (for UI display)
                await db.images.update(existingImageUuid, {
                    upload_status: 'confirmed',
                    sync_status: 'synced'
                    // Keep base64 so the image can still be displayed until next sync provides URL
                });
            } catch (confirmError) {
                // Handle duplicate errors same as uploadImage
                if (confirmError.isDuplicate || (confirmError.message && confirmError.message.includes('Image already exists'))) {
                    console.log(`Image ${existingImageUuid} update already confirmed on backend`);
                    await db.images.update(existingImageUuid, {
                        upload_status: 'confirmed',
                        sync_status: 'synced'
                        // Keep base64 so the image can still be displayed until next sync provides URL
                    });
                    return true;
                }
                // Set status to failed before re-throwing
                await db.images.update(existingImageUuid, {
                    upload_status: 'failed',
                    upload_error: `Confirm failed: ${confirmError.message}`
                });
                throw confirmError;
            }

            console.log(`Successfully updated image ${existingImageUuid}`);
            return true;

        } catch (error) {
            console.error('Error in updateImage:', error);
            
            // Handle CORS and policy errors (no retry)
            if (error.isCorsError || error.isPolicyError || error.permanent) {
                await db.images.update(existingImageUuid, { 
                    upload_status: 'failed',
                    upload_error: error.message
                });
                throw error;
            }
            
            // Retry logic for transient errors
            if (retryCount < MAX_RETRIES) {
                const delay = RETRY_DELAY_BASE * Math.pow(2, retryCount);
                console.log(`Retrying image update after ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);
                
                await db.images.update(existingImageUuid, { 
                    upload_status: 'retrying',
                    upload_error: `Retry ${retryCount + 1}/${MAX_RETRIES}: ${error.message}`
                });
                
                await new Promise(resolve => setTimeout(resolve, delay));
                return this.updateImage(existingImageUuid, newImageData, entityType, entityUuid, onProgress, retryCount + 1);
            }
            
            // Max retries exceeded
            await db.images.update(existingImageUuid, { 
                upload_status: 'failed',
                upload_error: error.message
            });

            // Queue for later if offline
            if (!this.isOnline) {
                await this.queueForLaterUpdate(existingImageUuid, newImageData, entityType, entityUuid);
            }

            throw error;
        }
    }

    /**
     * Queue image update for later processing when offline
     */
    async queueForLaterUpdate(existingImageUuid, newImageData, entityType, entityUuid) {
        const jobUuid = uuid();
        await db.jobs.put({
            uuid: jobUuid,
            type: 'image-s3-update',
            module: 'images',
            url: null,
            attempt: 0,
            payload: {
                uuid: jobUuid,
                existing_image_uuid: existingImageUuid,
                new_image_data: newImageData,
                imageable_uuid: entityUuid,
                entity_type: entityType
            },
            created_at: new Date().toISOString()
        });
    }

    /**
     * Decide how to sync an edited (annotated) image and queue the right job.
     *
     * The caller is expected to have already persisted the new base64 to
     * db.images for this uuid. This method only decides create-vs-update and
     * queues the matching job. Lives here (instead of in the editor view) so it
     * can be reused and unit-tested without mounting a component.
     *
     * @param {string} imageUuid - UUID of the edited image
     * @param {Object} editData - { base64, orientation }
     * @returns {Promise<{action: string, entityType: string}>} what was done
     */
    async queueEditedImageSync(imageUuid, { base64, orientation }) {
        const existingImage = await db.images.get(imageUuid);
        if (!existingImage) {
            throw new Error(`Image not found: ${imageUuid}`);
        }

        // Determine the parent entity type and whether it has been synced to the
        // server. findEntityInfo() covers all image-owning entity types
        // (measurement, observation, location_item_component, location).
        const entityInfo = await this.findEntityInfo(existingImage.imageable_uuid);
        let entityType = 'observation'; // default fallback
        let parentEntityExistsOnServer = false;
        if (entityInfo) {
            entityType = entityInfo.type;
            // `not_synced` is set on entities created locally that haven't reached
            // the server yet. Absence of the flag means it exists on the server.
            parentEntityExistsOnServer = !entityInfo.entity.not_synced;
        }

        const title = existingImage.title || null;
        const newImageData = { base64, title, orientation };

        if (!parentEntityExistsOnServer) {
            // Parent doesn't exist on the server yet. Don't queue anything - the
            // parent's create/sync flow (createImages / compressImages) uploads
            // this image once the parent is synced, picking up the saved base64.
            return { action: 'deferred-to-parent', entityType };
        }

        if (existingImage.upload_status === 'confirmed') {
            // The image itself already exists on the server -> replace it via an
            // update job. This is the only case where image-s3-update is valid.
            await this._addUpdateJob(imageUuid, existingImage.imageable_uuid, entityType, newImageData);
            return { action: 'update', entityType };
        }

        // Parent is on the server but the image itself is NOT confirmed yet (e.g. a
        // freshly added image annotated before its create finished). A plain update
        // would fail ("existing image not found for update"). We must also avoid
        // racing two creates: the online "immediate upload" path in compressImages
        // uploads directly WITHOUT a job and WITHOUT re-reading the DB.
        // Single pass over the queue (it can be large after a long offline period).
        const allJobs = await db.jobs.toArray();
        let uploadJob, updateJob;
        for (const job of allJobs) {
            if (!job.payload) continue;
            if (job.type === 'image-s3-upload' && job.payload.image_uuid === imageUuid) {
                uploadJob = job;
            } else if (job.type === 'image-s3-update' && job.payload.existing_image_uuid === imageUuid) {
                updateJob = job;
            }
            if (uploadJob && updateJob) break;
        }

        if (uploadJob) {
            // A create job is already queued. It re-reads base64 from the DB when it
            // runs, so it uploads the annotated version we just saved. Adding
            // anything here would create a second create, so do nothing.
            return { action: 'create-exists', entityType };
        }

        if (updateJob) {
            // An update job already exists (image annotated again before it ran).
            // Refresh its payload so the latest annotation wins; never queue a 2nd
            // job. Write a new object rather than mutating the in-memory copy.
            await db.jobs.put({
                ...updateJob,
                payload: { ...updateJob.payload, new_image_data: newImageData },
            });
            return { action: 'refresh-update', entityType };
        }

        // No job queued yet. An immediate (non-job) upload may be in progress with
        // the pre-annotation image, so queue an UPDATE rather than a CREATE.
        // updateImage() converts it to a create when the image turns out not to
        // exist on the backend, and defers while a create upload is in progress, so
        // we never race two creates.
        await this._addUpdateJob(imageUuid, existingImage.imageable_uuid, entityType, newImageData);
        return { action: 'queue-update', entityType };
    }

    /**
     * Helper: add an image-s3-update job for the given image.
     */
    async _addUpdateJob(existingImageUuid, imageableUuid, entityType, newImageData) {
        const jobUuid = uuid();
        await db.jobs.add({
            uuid: jobUuid,
            type: 'image-s3-update',
            module: 'images',
            url: null,
            attempt: 0,
            payload: {
                uuid: jobUuid,
                existing_image_uuid: existingImageUuid,
                new_image_data: newImageData,
                entity_type: entityType,
                imageable_uuid: imageableUuid,
            },
            created_at: new Date().toISOString(),
        });
    }
}

// Export singleton instance
export default new ImageUploadService();